// @ts-check
"use strict"

import { parseReportJson } from "@/utils/report-parser"

/**
 * @typedef {{ type: 'text', content: string }} ChartBlockText
 * @typedef {{ type: 'chart', raw: string }} ChartBlockChart
 * @typedef {{ type: 'chart-pending' }} ChartBlockPending
 * @typedef {ChartBlockText | ChartBlockChart | ChartBlockPending} ChartBlock
 */

/**
 * Splits a message string into alternating text and chart segments so that
 * chart JSON is never passed through the citation formatter or the markdown
 * pipeline (which would mangle `[1,2,3]` number arrays as citation groups).
 *
 * @param {string} text
 * @returns {ChartBlock[]}
 */
export function splitChartBlocks(text) {
  const CHART_RE = /<chart[\s\S]*?<\/chart>/g
  /** @type {ChartBlock[]} */
  const parts = []
  let lastIndex = 0
  let match

  while ((match = CHART_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) })
    }
    parts.push({ type: 'chart', raw: match[0] })
    lastIndex = CHART_RE.lastIndex
  }

  const remaining = text.slice(lastIndex)
  const pendingIdx = remaining.indexOf('<chart')
  if (pendingIdx !== -1) {
    if (pendingIdx > 0) {
      parts.push({ type: 'text', content: remaining.slice(0, pendingIdx) })
    }
    parts.push({ type: 'chart-pending' })
  } else if (remaining) {
    parts.push({ type: 'text', content: remaining })
  }

  return parts
}

/**
 * Parses a complete raw `<chart ...>...</chart>` string into a config, reading
 * attributes directly from the tag string so JSON content never touches an
 * HTML parser.
 *
 * @param {string} raw
 * @returns {import("./parseChartXml").ChartConfig | import("./parseChartXml").ChartParseError | null}
 */
export function parseChartRaw(raw) {
  const tagMatch = raw.match(/^<chart([\s\S]*?)>/)
  if (!tagMatch) return null

  /** @type {Record<string, string>} */
  const attrs = {}
  const attrRe = /(\w+)="([^"]*)"/g
  let m
  while ((m = attrRe.exec(tagMatch[1])) !== null) {
    attrs[m[1].toLowerCase()] = m[2]
  }

  const bodyMatch = raw.match(/<chart[^>]*>([\s\S]*?)<\/chart>/)
  if (!bodyMatch) return null

  return parseChartXml(attrs, bodyMatch[1].trim())
}

/**
 * @typedef {Object} ChartPayload
 * @property {string[]} [labels]
 * @property {Array<{ label?: string, type?: string, data: number[] | Array<{x: number, y: number}> }>} datasets
 */

/**
 * @typedef {Object} ChartConfig
 * @property {string} type
 * @property {string} title
 * @property {string | null} xLabel
 * @property {string | null} yLabel
 * @property {boolean} stacked
 * @property {number} height
 * @property {ChartPayload} payload
 */

/**
 * @typedef {Object} ChartParseError
 * @property {string} error
 * @property {string} title
 */

/**
 * Normalises the raw props and children string from react-markdown into a
 * typed config object. Uses the lenient parser so partial JSON during
 * streaming returns whatever data is available rather than throwing.
 *
 * Returns null for expected non-parseable states (streaming, missing required attrs).
 * Returns a ChartParseError when attributes are present but data parsing fails.
 *
 * @param {Record<string, string>} props - XML attributes extracted by react-markdown
 * @param {string} children - Raw JSON body (may be partial during streaming)
 * @returns {ChartConfig | ChartParseError | null}
 */
export function parseChartXml(props, children) {
  const type = props.type
  const title = props.title
  if (!type || !title) return null

  try {
    // HTML parsers lowercase all attribute names
    const xLabel = props.xlabel ?? props.xLabel ?? null
    const yLabel = props.ylabel ?? props.yLabel ?? null

    const payload = /** @type {ChartPayload} */ (parseReportJson(children ?? ""))
    if (!payload?.datasets) return null

    return {
      type,
      title,
      xLabel,
      yLabel,
      stacked: props.stacked === "true",
      height: props.height ? Number(props.height) : 280,
      payload,
    }
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e), title }
  }
}
